import useRequest from "@/lib/request/useRequest";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import {
    Modal,
    ModalHeader,
    ModalContent,
    ModalBody,
    ModalFooter,
    Listbox,
    ListboxItem,
    Button,
    Selection,
    Chip,
    Tabs,
    Tab,
    Spinner,
    Divider,
    Select,
    SelectItem,
} from "@heroui/react";
import {
    BarChart,
    Bar,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTableList,
    faCloudSunRain,
    faInfoCircle,
    faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
    PlantingPlan,
    ApplicationPlan,
    SimulatedCropPlan,
} from "@/types/cropPlan";
import { Organization } from "@/types/organization";
import { CropConfiguration } from "@/types/cropConfiguration";
import { NitrogenSimulationType, ProcessingStatus } from "@/types/enums";
import { Weather } from "@/types/weather";
import ScenarioPlannerIcon from "../../nav/ScenarioPlannerIcon";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import dayjs from "dayjs";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import TableV2 from "../../table/TableV2";
import { RankingCell } from "../../table/RankingCell";
import InfoModal from "../../common/InfoModal";
import { PlantingPlanForm } from "./PlantingPlanForm";
import { ApplicationPlanForm } from "./ApplicationPlanForm";


type TableType = SimulatedCropPlan & {
    penalty_early_CWAD_rank: number;
    penalty_NIAD_rank: number;
    penalty_remob_rank: number;
    penalty_roi_rank: number;
    penalty_early_NUPC_rank: number;
};

function FieldPlanner({
    plannerIsOpen,
    onClose,
    fieldId,
    activePlantingOperationId,
}: {
    plannerIsOpen: boolean;
    onClose: () => void;
    fieldId: number;
    activePlantingOperationId?: number;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();

    const { t } = useTranslation("insights");

    const { formatUnit } = useI18n();

    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: `/crops/`,
        enabled: plannerIsOpen,
    });

    const yearOptions: string[] = useMemo(() => {
        const currentYear = dayjs().year();
        const years = [`${currentYear} (Forecast)`];
        for (let i = 1; i < 10; i++) {
            years.push((currentYear - i).toString());
        }
        years.push("Decadal Mean");
        return years;
    }, []);

    const [selectedWeatherYears, setSelectedWeatherYears] = useState<Selection>(
        new Set([`${dayjs().year()} (Forecast)`]),
    );

    const [startDate, endDate] = useMemo(() => {
        const selectedYears = Array.from(selectedWeatherYears)
            .filter((year) => year !== "Decadal Mean")
            .map((year) =>
                year === `${dayjs().year()} (Forecast)`
                    ? dayjs().year()
                    : parseInt(year.toString()),
            );
        if (selectedYears.length === 0) {
            return [null, null];
        }
        const minYear = Math.min(...selectedYears);
        const maxYear = Math.max(...selectedYears);
        return [
            dayjs().year(minYear).startOf("year").toDate(),
            dayjs().year(maxYear).endOf("year").toDate(),
        ];
    }, [selectedWeatherYears]);

    const tenYearWeather = useGetRequest<Weather[]>({
        url: `/fields/${fieldId}/ten_year_weather/`,
        enabled:
            Array.from(selectedWeatherYears).includes("Decadal Mean") &&
            plannerIsOpen,
    });

    const fieldWeather = useGetRequest<Weather[]>({
        url: `/fields/${fieldId}/weather/`,
        params: {
            start_date: dayjs(startDate).format("YYYY-MM-DD"),
            end_date: dayjs(endDate).format("YYYY-MM-DD"),
        },
        enabled: plannerIsOpen && startDate !== null && endDate !== null,
    });

    const organizationsQuery = useGetRequest<Organization[]>({
        url: `/organizations/`,
        enabled: plannerIsOpen,
    });

    const organizationOptions = useMemo(() => {
        if (!organizationsQuery.data) {
            return [];
        }
        return organizationsQuery.data.map((org) => ({
            description: org.name,
            id: org.id,
        }));
    }, [organizationsQuery.data]);

    const cropPlansRequest = useGetRequest<SimulatedCropPlan[]>({
        url: `/simulated_crop_plans/`,
        params: {
            field: fieldId,
        },
        enabled: plannerIsOpen && !!fieldId,
    });

    const plantingPlansRequest = useGetRequest<PlantingPlan[]>({
        url: `/planting_plans/`,
        enabled: plannerIsOpen,
    });

    const applicationPlansRequest = useGetRequest<ApplicationPlan[]>({
        url: `/application_plans/`,
        enabled: plannerIsOpen,
    });

    const [selectedPlantingPlanId, setSelectedPlantingPlanId] =
        useState<Selection>(new Set([]));

    const plantingPlan = useMemo<PlantingPlan | null>(() => {
        const selectedId = Number(Array.from(selectedPlantingPlanId)[0]);
        return (
            plantingPlansRequest.data?.find((plan => plan.id === selectedId)) || null
        );
    }, [plantingPlansRequest.data, selectedPlantingPlanId]);

    const cropStageOptions = useMemo(() => {
        if (!cropsQuery.data || !plantingPlan?.crop_configuration) {
            return [];
        }
        const selectedCrop = cropsQuery.data.find(
            (crop) => crop.id === plantingPlan.crop_configuration,
        );
        if (selectedCrop) {
            return selectedCrop.stages.map((stage) => ({
                id: stage.id,
                description: stage.growth_stage,
            }));
        }

        return [];
    }, [cropsQuery.data, plantingPlan]);

    const [selectedApplicationPlanIds, setSelectedApplicationPlanIds] =
        useState<Selection>(new Set([]));

    const [selectedSimulations, setSelectedSimulations] = useState<Selection>(
        new Set([NitrogenSimulationType.Sentinel]),
    );

    const simulateCropPlansMutation = useMutateRequest<SimulatedCropPlan[]>({
        url: `/simulated_crop_plans/bulk_upsert/`,
        method: "put",
        successMessage: "Crop Plan Simulated",
        onSuccess: () => {
            cropPlansRequest.refetch();
            plantingPlansRequest.refetch();
            applicationPlansRequest.refetch();
        },
    });

    const cropPlans: SimulatedCropPlan[] = useMemo(() => {
        const selectedSimulationType = Array.from(selectedSimulations)[0];
        const selectedApplicationPlans = Array.from(
            selectedApplicationPlanIds,
        ).map((key) =>
            applicationPlansRequest.data?.find(
                (plan) =>
                    (plan.id && plan.id.toString() === key) ||
                    (plan.id === null && key === "null"),
            ),
        );
        if (!plantingPlan || selectedApplicationPlans.length === 0) {
            return [];
        }
        return selectedApplicationPlans.map((applicationPlan) => {
            const cropPlan = cropPlansRequest.data?.find(
                (plan) =>
                    plan.application_plan?.id === applicationPlan?.id &&
                    plan.planting_plan?.id === plantingPlan.id &&
                    plan.simulation_type === selectedSimulationType,
            );
            return {
                id: cropPlan?.id || null,
                is_primary: cropPlan ? cropPlan.is_primary : false,
                field: fieldId,
                planting_plan: plantingPlan && {
                    ...plantingPlan,
                    date: dayjs(plantingPlan.date).format("YYYY-MM-DD"),
                },
                application_plan: applicationPlan || null,
                simulate_rates: !cropPlan?.id,
                total_nitrogen_applied: cropPlan?.total_nitrogen_applied,
                total_nitrogen_uptake: cropPlan?.total_nitrogen_uptake,
                final_harvestable_weight: cropPlan?.final_harvestable_weight,
                penalty_early_CWAD: cropPlan?.penalty_early_CWAD,
                penalty_NIAD: cropPlan?.penalty_NIAD,
                penalty_remob: cropPlan?.penalty_remob,
                penalty_roi: cropPlan?.penalty_roi,
                penalty_early_NUPC: cropPlan?.penalty_early_NUPC,
                created_by: cropPlan?.created_by,
                created_date: cropPlan?.created_date,
                last_modified_by: cropPlan?.last_modified_by,
                last_modified_date: cropPlan?.last_modified_date,
                simulated_rates: cropPlan?.simulated_rates,
                simulation_type:
                    selectedSimulationType as NitrogenSimulationType,
                processing_status: cropPlan?.processing_status,
            };
        });
    }, [
        selectedApplicationPlanIds,
        applicationPlansRequest.data,
        cropPlansRequest.data,
        fieldId,
        plantingPlan,
        selectedSimulations,
    ]);

    const cropPlanRankings: TableType[] = useMemo(() => {
        const values = {
            penalty_early_CWAD: cropPlans.map(
                (plan) => plan.penalty_early_CWAD || Infinity,
            ),
            penalty_NIAD: cropPlans.map(
                (plan) => plan.penalty_NIAD || Infinity,
            ),
            penalty_remob: cropPlans.map(
                (plan) => plan.penalty_remob || Infinity,
            ),
            penalty_roi: cropPlans.map((plan) => plan.penalty_roi || Infinity),
            penalty_early_NUPC: cropPlans.map(
                (plan) => plan.penalty_early_NUPC || Infinity,
            ),
        };
        const rankings: { [key: string]: number[] } = {};
        Object.entries(values).forEach(([key, vals]) => {
            const sorted = [...vals].sort((a, b) => a - b);
            rankings[key] = vals.map((val) => sorted.indexOf(val) + 1);
        });
        return cropPlans.map((plan, index) => ({
            ...plan,
            penalty_early_CWAD_rank: rankings["penalty_early_CWAD"][index],
            penalty_NIAD_rank: rankings["penalty_NIAD"][index],
            penalty_remob_rank: rankings["penalty_remob"][index],
            penalty_roi_rank: rankings["penalty_roi"][index],
            penalty_early_NUPC_rank: rankings["penalty_early_NUPC"][index],
        }));
    }, [cropPlans]);

    const primaryCropPlan = useMemo(() => {
        if (!cropPlansRequest.data) {
            return null;
        }
        return cropPlansRequest.data.find((plan) => plan.is_primary);
    }, [cropPlansRequest.data]);

    const comparisonColumns: ColumnDef<TableType>[] = [
        {
            id: "application_plan.name",
            header: "Plan",
            accessorFn: (row) => row.application_plan?.name,
            cell: ({ row }) => (
                <span className="text-base font-semibold">
                    {row.original.application_plan?.name}
                </span>
            ),
            size: 250,
        },
        {
            id: "penalty_early_CWAD_rank",
            header: "Early CWAD",
            accessorFn: (row) => row.penalty_early_CWAD_rank,
            cell: ({ row }) => (
                <RankingCell value={row.original.penalty_early_CWAD_rank} />
            ),
            size: 150,
        },
        {
            id: "penalty_NIAD_rank",
            header: "NIAD",
            accessorFn: (row) => row.penalty_NIAD_rank,
            cell: ({ row }) => (
                <RankingCell value={row.original.penalty_NIAD_rank} />
            ),
            size: 150,
        },
        {
            id: "penalty_remob_rank",
            header: "Remob",
            accessorFn: (row) => row.penalty_remob_rank,
            cell: ({ row }) => (
                <RankingCell value={row.original.penalty_remob_rank} />
            ),
            size: 150,
        },
        {
            id: "penalty_roi_rank",
            header: "ROI",
            accessorFn: (row) => row.penalty_roi_rank,
            cell: ({ row }) => (
                <RankingCell value={row.original.penalty_roi_rank} />
            ),
            size: 150,
        },
        {
            id: "penalty_early_NUPC_rank",
            header: "Early NUPC",
            accessorFn: (row) => row.penalty_early_NUPC_rank,
            cell: ({ row }) => (
                <RankingCell value={row.original.penalty_early_NUPC_rank} />
            ),
            size: 150,
        },
    ];

    const [sorting, setSorting] = useState<SortingState>([
        { id: "penalty_early_CWAD_rank", desc: false },
    ]);

    const [comparisonView, setComparisonView] = useState<boolean>(false);

    const simulationOptions = [
        {
            key: NitrogenSimulationType.Sentinel,
            label: "Sentinel",
        },
        {
            key: NitrogenSimulationType.Drought,
            label: "Drought",
        },
        {
            key: NitrogenSimulationType.Wet,
            label: "Wet",
        },
        {
            key: NitrogenSimulationType.Decadal90,
            label: "Decadal 90",
        },
        {
            key: NitrogenSimulationType.Decadal50,
            label: "Decadal 50",
        },
    ];

    return (
        <Modal
            isOpen={plannerIsOpen}
            onClose={onClose}
            className="max-w-8xl h-[90vh]"
            scrollBehavior="inside"
        >
            <ModalContent>
                <ModalHeader>
                    <div className="flex flex-row items-center gap-4 font-bold text-3xl text-primary-500 ml-4">
                        <img
                            src={allNHorizontalImage}
                            alt="All N Horizontal"
                            className="h-10 object-cover"
                        />
                        Planner
                    </div>
                </ModalHeader>
                <ModalBody className="mx-4">
                    <Tabs
                        size="lg"
                        color="primary"
                        variant="underlined"
                        fullWidth
                    >
                        <Tab
                            key="plans"
                            title={
                                <div>
                                    <FontAwesomeIcon icon={faTableList} /> Plans
                                </div>
                            }
                        >
                            {comparisonView ? (
                                <div className="mx-auto mt-4 mb-4 w-2/3">
                                    <div className="flex flex-row justify-center items-center mb-4">
                                        <InfoModal
                                            trigger={(setShowModal) => (
                                                <div className="flex flex-row items-center w-full gap-2 text-sm">
                                                    Performance Rankings
                                                    <Button
                                                        onPress={() =>
                                                            setShowModal(true)
                                                        }
                                                        size="sm"
                                                        variant="light"
                                                        color="primary"
                                                        isIconOnly
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faInfoCircle}
                                                        />
                                                    </Button>
                                                </div>
                                            )}
                                        >
                                            <ModalHeader className="font-semibold text-xl">
                                                Penalties
                                            </ModalHeader>
                                            <ModalBody>
                                                <p>
                                                    The penalties are calculated
                                                    based on the following
                                                    criteria:
                                                </p>
                                            </ModalBody>
                                        </InfoModal>
                                    </div>
                                    <TableV2
                                        data={cropPlanRankings}
                                        columns={comparisonColumns}
                                        sorting={sorting}
                                        setSorting={setSorting}
                                        manualSorting={false}
                                        isLoading={
                                            simulateCropPlansMutation.isPending
                                        }
                                    />
                                </div>
                            ) : (
                                <>
                                    <PlantingPlanForm
                                        organizations={organizationOptions}
                                        activePlantingOperationId={activePlantingOperationId}
                                        selectedPlantingPlanId={selectedPlantingPlanId}
                                        setSelectedPlantingPlanId={
                                            setSelectedPlantingPlanId
                                        }
                                        plantingPlansRequest={plantingPlansRequest}
                                        cropsQuery={cropsQuery}
                                        primaryCropPlan={primaryCropPlan || undefined}
                                    />
                                    <Divider className="my-3" />
                                    <ApplicationPlanForm
                                        fields={[{ id: fieldId, active_planting_operation: activePlantingOperationId ? { id: activePlantingOperationId } : null }]}
                                        organizations={organizationOptions}
                                        selectedApplicationPlanIds={selectedApplicationPlanIds}
                                        setSelectedApplicationPlanIds={setSelectedApplicationPlanIds}
                                        applicationPlansRequest={applicationPlansRequest}
                                        cropStageOptions={cropStageOptions}
                                        plantingPlan={plantingPlan!}
                                        cropPlansRequest={cropPlansRequest}
                                        selectedSimulations={selectedSimulations}
                                    />
                                </>
                            )}
                        </Tab>
                        <Tab
                            key="weather"
                            title={
                                <div>
                                    <FontAwesomeIcon icon={faCloudSunRain} />{" "}
                                    Weather
                                </div>
                            }
                        >
                            <div className="flex flex-row w-full justify-between gap-2">
                                <div className="flex flex-col min-w-[200px]">
                                    <Listbox
                                        items={yearOptions.map((year) => ({
                                            id: year,
                                            description: year.toString(),
                                        }))}
                                        selectionMode="multiple"
                                        color="primary"
                                        variant="flat"
                                        className="border border-gray-300 max-h-[250px] overflow-scroll"
                                        selectedKeys={selectedWeatherYears}
                                        onSelectionChange={
                                            setSelectedWeatherYears
                                        }
                                    >
                                        {(item) => (
                                            <ListboxItem key={item.id!}>
                                                {item.description}
                                            </ListboxItem>
                                        )}
                                    </Listbox>
                                </div>
                                {tenYearWeather.isLoading ||
                                    fieldWeather.isLoading ? (
                                    <div className="flex flex-1 w-full h-72 justify-center items-center">
                                        <Spinner size="lg" color="primary" />
                                    </div>
                                ) : (
                                    <div className="flex flex-col w-full gap-2">
                                        {Array.from(selectedWeatherYears).map(
                                            (year) => (
                                                <>
                                                    <div className="text-lg font-bold text-center">
                                                        {year}
                                                    </div>
                                                    <div className="flex flex-row w-full h-52">
                                                        <ResponsiveContainer>
                                                            <LineChart
                                                                data={
                                                                    year ===
                                                                        "Decadal Mean"
                                                                        ? tenYearWeather.data
                                                                        : fieldWeather.data?.filter(
                                                                            (
                                                                                weather,
                                                                            ) =>
                                                                                dayjs(
                                                                                    weather.date,
                                                                                )
                                                                                    .year()
                                                                                    .toString() ===
                                                                                year,
                                                                        )
                                                                }
                                                                margin={{
                                                                    top: 5,
                                                                    right: 20,
                                                                    left: 10,
                                                                    bottom: 5,
                                                                }}
                                                            >
                                                                <XAxis
                                                                    dataKey="date"
                                                                    ticks={[
                                                                        `${year === "Decadal Mean" ? "2025" : year}-01-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-02-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-03-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-04-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-05-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-06-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-07-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-08-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-09-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-10-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-11-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-12-01`,
                                                                    ]}
                                                                    tickFormatter={(
                                                                        date,
                                                                    ) =>
                                                                        dayjs(
                                                                            date,
                                                                        ).format(
                                                                            "MMM",
                                                                        )
                                                                    }
                                                                />
                                                                <YAxis
                                                                    label={{
                                                                        value: `${t("temperature")} ${formatUnit("F", "C")}`,
                                                                        angle: -90,
                                                                        dx: -30,
                                                                    }}
                                                                    fontSize={
                                                                        12
                                                                    }
                                                                />
                                                                <Tooltip />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="max_temperature"
                                                                    stroke="#FF0000"
                                                                    name="Max Temperature"
                                                                    dot={false}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="min_temperature"
                                                                    stroke="#0000FF"
                                                                    name="Min Temperature"
                                                                    dot={false}
                                                                />
                                                            </LineChart>
                                                        </ResponsiveContainer>
                                                        <ResponsiveContainer>
                                                            <BarChart
                                                                data={
                                                                    year ===
                                                                        "Decadal Mean"
                                                                        ? tenYearWeather.data
                                                                        : fieldWeather.data?.filter(
                                                                            (
                                                                                weather,
                                                                            ) =>
                                                                                dayjs(
                                                                                    weather.date,
                                                                                )
                                                                                    .year()
                                                                                    .toString() ===
                                                                                year,
                                                                        )
                                                                }
                                                                margin={{
                                                                    top: 5,
                                                                    right: 20,
                                                                    left: 10,
                                                                    bottom: 5,
                                                                }}
                                                            >
                                                                <XAxis
                                                                    dataKey="date"
                                                                    ticks={[
                                                                        `${year === "Decadal Mean" ? "2025" : year}-01-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-02-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-03-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-04-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-05-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-06-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-07-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-08-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-09-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-10-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-11-01`,
                                                                        `${year === "Decadal Mean" ? "2025" : year}-12-01`,
                                                                    ]}
                                                                    tickFormatter={(
                                                                        date,
                                                                    ) =>
                                                                        dayjs(
                                                                            date,
                                                                        ).format(
                                                                            "MMM",
                                                                        )
                                                                    }
                                                                />
                                                                <YAxis
                                                                    label={{
                                                                        value: `${t("chartLabels.precipitation")} (${formatUnit("in", "mm")})`,
                                                                        angle: -90,
                                                                        dx: -30,
                                                                    }}
                                                                    fontSize={
                                                                        12
                                                                    }
                                                                />
                                                                <Tooltip />
                                                                <Bar
                                                                    dataKey="precipitation"
                                                                    fill="#00AAFF"
                                                                    name="Precipitation"
                                                                />
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </>
                                            ),
                                        )}
                                    </div>
                                )}
                            </div>
                        </Tab>
                    </Tabs>
                </ModalBody>
                <ModalFooter className="mr-6 flex flex-col">
                    {cropPlans.filter((plan) => plan.processing_status === ProcessingStatus.Failed).length > 0 && (
                        <div className="flex flex-row items-center gap-2 w-full justify-end">
                            <Chip color="danger" variant="flat">
                                <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    className="mr-1"
                                />
                                <span className="text-red-600">
                                    Error simulating one or more plans.
                                </span>
                            </Chip>
                        </div>
                    )}
                    {(Array.from(selectedPlantingPlanId).length === 0 ||
                        Array.from(selectedApplicationPlanIds).length ===
                        0) && (
                            <div className="flex flex-row items-center gap-2 w-full justify-end">
                                {Array.from(selectedPlantingPlanId).length ===
                                    0 && (
                                        <Chip color="warning" variant="flat">
                                            Select a Planting Plan
                                        </Chip>
                                    )}
                                {Array.from(selectedApplicationPlanIds).length ===
                                    0 && (
                                        <Chip color="warning" variant="flat">
                                            Select an Application Plan
                                        </Chip>
                                    )}
                            </div>
                        )}
                    <div className="flex flex-row gap-2 items-center w-full justify-end">
                        <Select
                            className="max-w-xs"
                            size="sm"
                            items={simulationOptions}
                            label="Simulation"
                            labelPlacement="inside"
                            isDisabled={
                                Array.from(selectedApplicationPlanIds).length ===
                                0 ||
                                Array.from(selectedPlantingPlanId).length === 0
                            }
                            selectedKeys={selectedSimulations}
                            onSelectionChange={setSelectedSimulations}
                            variant="bordered"
                            color="primary"
                        >
                            {(item) => (
                                <SelectItem key={item.key}>{item.label}</SelectItem>
                            )}
                        </Select>
                        <Button
                            color="primary"
                            isDisabled={
                                Array.from(selectedApplicationPlanIds).length ===
                                0 ||
                                Array.from(selectedPlantingPlanId).length === 0
                            }
                            isLoading={simulateCropPlansMutation.isPending}
                            onPress={() => {
                                if (
                                    !comparisonView &&
                                    cropPlans?.find((plan) => !plan.id)
                                ) {
                                    simulateCropPlansMutation.mutate({
                                        data: cropPlans,
                                    });
                                }
                                setComparisonView(!comparisonView);
                            }}
                        >
                            <FontAwesomeIcon icon={faTableList} size="xl" />
                            {comparisonView
                                ? "Edit Plans"
                                : `Compare ${Array.from(selectedApplicationPlanIds).length
                                } Plans`}
                        </Button>
                        <Button
                            color="primary"
                            isDisabled={
                                Array.from(selectedApplicationPlanIds).length ===
                                0 ||
                                Array.from(selectedPlantingPlanId).length === 0
                            }
                            isLoading={simulateCropPlansMutation.isPending}
                            onPress={() => {
                                if (
                                    !cropPlans
                                ) {
                                    return;
                                }
                                simulateCropPlansMutation.mutate({
                                    data: cropPlans.map((plan) => ({
                                        ...plan,
                                        simulate_rates: true,
                                    })),
                                });
                            }}
                        >
                            <ScenarioPlannerIcon color="white" />
                            {`Get Rates for ${Array.from(selectedApplicationPlanIds).length
                                } Plans`}
                        </Button>
                    </div>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default FieldPlanner;
